import React, { useMemo, useRef, useEffect } from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaRegClock, FaHome, FaTicketAlt, FaInfoCircle, FaUser, FaPlus, FaTrash } from 'react-icons/fa';
import EventImage from '../../../../components/EventImage.jsx';
import LocationPicker from '../../../../components/LocationPicker.jsx';

// Editable clone of the public CustomerViewEvent card.
// This component is intentionally driven by the organiser EventHost form state
// (useEventFormController): formData, tabs, packages, and cover image.
// It does NOT fetch, and it does NOT know about routing.
//
// Props expected (all from useEventFormController):
// - formData: { name, location, startdate, enddate, start_time, end_time, description, type, capacity, ... }
// - tabs:     [ { name, content }, ... ]
// - packages: same shape as organiser packages (packageType, location, duration, startDate, endDate, pricing, details, typeOptions)
// - coverImageFile: File | null
// - onSelectTab(index): sets the active tab in parent state
// - activeTab: number
// - handlers for edits:
//     handleInputChange, handlePackageChange, addPackage, addTab, handleTabChange
// - onImageChange: input change handler for the cover image (you can reuse handleCoverImageChange)
// - placeholderCoverUrl: fallback image path when no image selected

const EditableCustomerEventCard = ({
  formData,
  tabs,
  packages,
  coverImageFile,
  activeTab,
  onSelectTab,
  handleInputChange,
  handlePackageChange,
  addPackage,
  addTab,
  handleTabChange,
  removePackage,
  removeTab,
  onImageChange,
  onBackgroundImageChange,
  eventTypeOptions = [],
  handleEventTypeChange,
  customEventType,
  handleCustomEventTypeChange,
  placeholderCoverUrl = '/fallback-logo.png',
  backgroundMode,
  setBackgroundMode,
  imageBgColor,
  setImageBgColor,
  imageBgColor2,
  setImageBgColor2,
  bgImageUrl,
  setBgImageUrl,
}) => {
  // Local copy of the inline styles from CustomerViewEvent to preserve look & feel.
  const styles = useMemo(() => ({
    eventCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      marginBottom: '2rem',
      transition: 'all 0.3s ease',
    },
    eventImageContainer: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '8px 8px 0 0',
      width: '100%',
      // Base height; will be overridden to a taller value when an image is present
      height: '200px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    eventImage: {
      maxWidth: '100%',
      maxHeight: '100%',
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
    },
    imageOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'rgba(0,0,0,0.35)',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'opacity 0.2s ease',
      pointerEvents: 'none',
    },
    imageDropZone: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontSize: '0.9rem',
      textAlign: 'center',
      padding: '0 1rem',
      cursor: 'pointer',
      background: 'transparent',
    },
    eventDetails: {
      padding: '1.5rem',
    },
    eventInfo: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    eventInfoItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#6c757d',
      fontSize: '0.9rem',
    },
    editableInfoInput: {
      border: 'none',
      borderBottom: '1px dashed #ced4da',
      backgroundColor: 'transparent',
      outline: 'none',
      fontSize: '0.9rem',
      color: '#495057',
      minWidth: '120px',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #dee2e6',
      marginBottom: '1.5rem',
      width: '100%',
      overflowX: 'auto',
      overflowY: 'hidden',
      whiteSpace: 'nowrap',
    },
    tab: {
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      color: '#6c757d',
      borderTop: 'none',
      borderRight: 'none',
      borderLeft: 'none',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s ease',
      background: 'none',
      fontSize: '1rem',
      fontWeight: '500',
      flex: '1',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
      margin: '0 0.25rem',
    },
    activeTab: {
      color: '#e4b34b',
      border: 'none',
      borderBottom: '2px solid #fcf0d7',
      fontWeight: '600',
      boxShadow: 'none',
      outline: 'none',
    },
    tabPlusButton: {
      flex: '0 0 auto',
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      color: '#EFB034FF',
      border: 'none',
      background: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: 600,
    },
    tabContent: {
      marginBottom: '1.5rem',
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0 0 8px 8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      minHeight: '200px',
    },
    eventDescription: {
      width: '100%',
      minHeight: '100px',
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      padding: '0.75rem 1rem',
      resize: 'vertical',
      fontSize: '0.95rem',
      color: '#495057',
    },
    eventDetailItem: {
      marginBottom: '0.75rem',
      fontSize: '1rem',
      color: '#495057',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    smallInput: {
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '4px 8px',
      fontSize: '0.85rem',
      outline: 'none',
      minWidth: '120px',
      flex: 1,
      width: '100%',
    },
    contactInfo: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
    },
    contactItem: {
      marginTop: '0.75rem',
      fontSize: '1rem',
      color: '#495057',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    contactInput: {
      flex: 1,
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '4px 8px',
      fontSize: '0.9rem',
      outline: 'none',
    },
    packagesGrid: {
      display: 'grid',
      // Slightly wider cards so date range and duration text fit neatly
      // but still compact on smaller screens
      gridTemplateColumns: 'repeat(auto-fill, minmax(295px, 1fr))',
      gap: '1.5rem',
    },
    packageCard: {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #f5c874',
      position: 'relative',
      gap: '0.5rem',
    },
    addPackageCard: {
      borderStyle: 'dashed',
      borderWidth: '2px',
      borderColor: '#EFB034FF',
      backgroundColor: '#fffbf0',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      textAlign: 'center',
      color: '#EFB034FF',
      display: 'flex',
    },
    packageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
      borderBottom: '1px solid #f1f3f5',
      paddingBottom: '0.5rem',
      gap: '0.75rem',
    },
    packageNameInput: {
      border: 'none',
      borderBottom: '1px dashed #ced4da',
      outline: 'none',
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#2c3e50',
      minWidth: '150px',
    },
    packageTicketsContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.8rem',
      color: '#6c757d',
    },
    packageTicketsInput: {
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '2px 6px',
      fontSize: '0.8rem',
      outline: 'none',
      width: '70px',
    },
    packageDescription: {
      fontSize: '0.9rem',
      color: '#6c757d',
      minHeight: '60px',
      borderRadius: '6px',
      border: '1px solid #dee2e6',
      padding: '0.5rem 0.75rem',
      resize: 'none',
      overflow: 'hidden',
    },
    packageDetails: {
      marginBottom: '0.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    },
    packageDetail: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '0.85rem',
      color: '#6c757d',
    },
    packageDetailInput: {
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '2px 6px',
      fontSize: '0.8rem',
      outline: 'none',
      minWidth: '120px',
    },
    packageBottom: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      marginTop: 'auto',
      paddingTop: '0.75rem',
      borderTop: '1px solid #f1f3f5',
      gap: '0.75rem',
    },
    packagePriceInput: {
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '4px 8px',
      fontSize: '0.9rem',
      outline: 'none',
      minWidth: '90px',
    },
  }), []);

  const imageBackgroundStyle = useMemo(() => {
    if (backgroundMode === 'gradient') {
      return {
        backgroundImage: `linear-gradient(90deg, ${imageBgColor}, ${imageBgColor2})`,
        backgroundColor: 'transparent',
      };
    }

    if (backgroundMode === 'image' && bgImageUrl) {
      return {
        backgroundImage: `url(${bgImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
      };
    }

    // solid (default)
    return {
      backgroundColor: imageBgColor,
      backgroundImage: 'none',
    };
  }, [backgroundMode, imageBgColor, imageBgColor2, bgImageUrl]);

  const isImageEditable = !!onImageChange;

  // Compute preview URL for cover image
  const coverImageUrl = useMemo(() => {
    if (coverImageFile instanceof File) {
      return URL.createObjectURL(coverImageFile);
    }
    // fall back to placeholder while organiser is editing
    return placeholderCoverUrl;
  }, [coverImageFile, placeholderCoverUrl]);

  const handleBasicChange = (field, value) => {
    if (!handleInputChange) return;
    
    // Validate dates to prevent past dates (start date must be tomorrow or later)
    if (field === 'startdate' || field === 'enddate') {
      if (value && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const selectedDate = new Date(value);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        
        if (Number.isNaN(selectedDate.getTime())) {
          return;
        }
        
        if (selectedDate < tomorrow) {
          // Don't allow dates before tomorrow
          return;
        }
      }
    }
    
    // Validate end date against start date
    if (field === 'enddate' && value && value.match(/^\d{4}-\d{2}-\d{2}$/) && formData.startdate) {
      const endDate = new Date(value);
      const startDate = new Date(formData.startdate);
      
      if (!Number.isNaN(endDate.getTime()) && !Number.isNaN(startDate.getTime()) && endDate < startDate) {
        return;
      }
    }
    
    // If start date changes, ensure end date is at least start date
    if (field === 'startdate' && value && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const newStartDate = new Date(value);
      const currentEndDate = formData.enddate ? new Date(formData.enddate) : null;
      
      if (!Number.isNaN(newStartDate.getTime())) {
        // Always update end date to be at least start date
        if (!currentEndDate || currentEndDate < newStartDate) {
          // Update the end date to match the new start date
          handleInputChange({ target: { name: 'enddate', value } });
        }
      }
    }
    
    // Validate times to prevent past times on same day
    if (field === 'start_time' || field === 'end_time') {
      const selectedTime = value;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentDate = formData.startdate || formData.enddate;
      
      if (currentDate) {
        const eventDate = new Date(currentDate);
        if (eventDate.toDateString() === today.toDateString()) {
          const [hours, minutes] = selectedTime.split(':');
          const currentTime = new Date();
          const currentHours = currentTime.getHours();
          const currentMinutes = currentTime.getMinutes();
          
          // Don't allow past times on the same day
          if (hours < currentHours || (hours === currentHours && minutes < currentMinutes)) {
            return;
          }
        }
      }
    }
    
    // Validate end time against start time for same day
    if (field === 'end_time' && value && formData.start_time && formData.startdate === formData.enddate) {
      const [endHours, endMinutes] = value.split(':');
      const [startHours, startMinutes] = formData.start_time.split(':');
      
      const endTimeInMinutes = parseInt(endHours) * 60 + parseInt(endMinutes);
      const startTimeInMinutes = parseInt(startHours) * 60 + parseInt(startMinutes);
      
      if (endTimeInMinutes <= startTimeInMinutes) {
        return;
      }
    }
    
    handleInputChange({ target: { name: field, value } });
  };

  const handleTabTitleChange = (index, value) => {
    if (!handleTabChange) return;
    handleTabChange(index, 'name', value);
  };

  const handleTabContentChange = (index, value) => {
    if (!handleTabChange) return;
    handleTabChange(index, 'content', value);
  };

  const handlePackageFieldChange = (index, field, value) => {
    if (!handlePackageChange) return;
    
    // Validate validity dates to be within event start and end dates
    if (field === 'validFromDate' || field === 'validToDate') {
      const selectedDate = new Date(value);
      
      // Don't allow package validity dates before event starts
      if (formData.startdate) {
        const eventStartDate = new Date(formData.startdate);
        if (!Number.isNaN(eventStartDate.getTime()) && selectedDate < eventStartDate) {
          return;
        }
      }
      
      // Don't allow package validity dates after event ends
      if (formData.enddate) {
        const eventEndDate = new Date(formData.enddate);
        if (!Number.isNaN(eventEndDate.getTime()) && selectedDate > eventEndDate) {
          return;
        }
      }
    }
    
    // Validate end dates against start dates for packages
    const pkg = packages[index];
    
    if (field === 'endDate' && value && pkg && pkg.startDate) {
      const endDate = new Date(value);
      const startDate = new Date(pkg.startDate);
      
      if (endDate < startDate) {
        return;
      }
    }
    
    // If start date changes, ensure end date is at least the start date
    if (field === 'startDate' && value) {
      const newStartDate = new Date(value);
      const currentEndDate = pkg && pkg.endDate ? new Date(pkg.endDate) : null;
      
      // Always update end date to be at least the start date
      if (!currentEndDate || currentEndDate < newStartDate) {
        // Update the end date to match the new start date
        handlePackageChange(index, 'endDate', value);
      }
    }
    
    // Validate validity period end date against start date
    if (field === 'validToDate' && value && pkg && pkg.validFromDate) {
      const endDate = new Date(value);
      const startDate = new Date(pkg.validFromDate);
      
      if (endDate < startDate) {
        return;
      }
    }
    
    // If validity start date changes, ensure end date is at least the start date
    if (field === 'validFromDate' && value) {
      const newStartDate = new Date(value);
      const currentEndDate = pkg && pkg.validToDate ? new Date(pkg.validToDate) : null;
      
      // Always update end date to be at least the start date
      if (!currentEndDate || currentEndDate < newStartDate) {
        // Update the end date to match the new start date
        handlePackageChange(index, 'validToDate', value);
      }
    }
    
    handlePackageChange(index, field, value);
  };

  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  const computePackageDurationDays = (pkg) => {
    if (!pkg || !pkg.validFromDate || !pkg.validToDate) return '';

    const start = new Date(pkg.validFromDate);
    const end = new Date(pkg.validToDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

    const msPerDay = 1000 * 60 * 60 * 24;
    const diff = (end - start) / msPerDay;

    if (diff < 0) return '';

    // Inclusive day count (e.g. 1-day package when from/to are the same)
    return Math.floor(diff) + 1;
  };

  const renderTabsHeader = () => {
    const labels = ['Information', 'Packages', 'Terms and Conditions'];
    return (
      <div style={styles.tabs}>
        {labels.map((label, index) => (
          <button
            key={label}
            type="button"
            className="editable-event-tab"
            style={{
              ...styles.tab,
              ...(activeTab === index ? styles.activeTab : {}),
            }}
            onClick={() => onSelectTab && onSelectTab(index)}
          >
            {label}
          </button>
        ))}

        {/* Custom tabs added by organiser (after the fixed three) */}
        {tabs && tabs.map((tab, idx) => {
          const tabIndex = 3 + idx;
          return (
            <button
              key={`custom-tab-${idx}`}
              type="button"
              className="editable-event-tab"
              style={{
                ...styles.tab,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                ...(activeTab === tabIndex ? styles.activeTab : {}),
              }}
              onClick={() => onSelectTab && onSelectTab(tabIndex)}
            >
              <input
                type="text"
                value={tab.name}
                onChange={(e) => handleTabTitleChange(idx, e.target.value)}
                placeholder={`Tab ${idx + 1}`}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  outline: 'none',
                  color: 'inherit',
                  fontWeight: 'inherit',
                  width: 'auto',
                  maxWidth: '120px',
                }}
              />
              {removeTab && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTab(idx);
                  }}
                  style={{
                    marginLeft: '4px',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: '0.9rem',
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: '16px',
                    height: '16px',
                  }}
                  aria-label={`Remove tab ${idx + 1}`}
                  title={`Remove tab ${idx + 1}`}
                >
                  <FaTrash />
                </span>
              )}
            </button>
          );
        })}

        {addTab && (
          <button
            type="button"
            style={styles.tabPlusButton}
            onClick={() => addTab()}
          >
            <FaPlus /> Add Tab
          </button>
        )}
      </div>
    );
  };

  const renderAboutTab = () => (
    <div>
      {/* Event Name section */}
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Event Name</h3>
        <input
          type="text"
          style={{ ...styles.smallInput, width: '100%' }}
          value={formData.name || ''}
          onChange={(e) => handleBasicChange('name', e.target.value)}
          placeholder="Enter the name of your event"
        />
      </div>

      <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>About This Event</h3>
      <textarea
        style={styles.eventDescription}
        value={formData.description || ''}
        onChange={(e) => handleBasicChange('description', e.target.value)}
        placeholder="Describe your event so customers know what to expect"
      />
      {/* Start / End date */}
      <div className="editable-event-pair-row" style={{ ...styles.eventDetailItem, gap: '20px' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong style={{ whiteSpace: 'nowrap', width: '90px' }}>Start Date:</strong>
          <input
            type="date"
            min={(() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return tomorrow.toISOString().split('T')[0];
            })()}
            style={styles.smallInput}
            value={formData.startdate || ''}
            onChange={(e) => handleBasicChange('startdate', e.target.value)}
          />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong style={{ whiteSpace: 'nowrap', width: '90px' }}>End Date:</strong>
          <input
            type="date"
            min={formData.startdate || new Date().toISOString().split('T')[0]}
            style={styles.smallInput}
            value={formData.enddate || ''}
            onChange={(e) => handleBasicChange('enddate', e.target.value)}
          />
        </div>
      </div>
      {/* Start / End time */}
      <div className="editable-event-pair-row" style={{ ...styles.eventDetailItem, gap: '20px' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong style={{ whiteSpace: 'nowrap', width: '90px' }}>Start Time:</strong>
          <input
            type="time"
            style={styles.smallInput}
            value={formData.start_time || ''}
            onChange={(e) => handleBasicChange('start_time', e.target.value)}
          />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong style={{ whiteSpace: 'nowrap', width: '90px' }}>End Time:</strong>
          <input
            type="time"
            style={styles.smallInput}
            value={formData.end_time || ''}
            onChange={(e) => handleBasicChange('end_time', e.target.value)}
          />
        </div>
      </div>
      <div style={styles.eventDetailItem}>
        <strong style={{ whiteSpace: 'nowrap', width: '90px' }}>Location:</strong>
        <div style={{ flex: 1 }}>
          <LocationPicker
            value={formData.location || null}
            onChange={(locationData) => {
              if (handleInputChange && handleInputChange.toString() !== '() => {}') {
                console.log('EditableCustomerEventCard received location data:', locationData);
                handleBasicChange('location', locationData);
              }
            }}
            placeholder="Search for event location..."
          />
        </div>
      </div>
      <div style={styles.eventDetailItem}>
        <strong style={{ whiteSpace: 'nowrap', width: '90px' }}>Event Type:</strong>
        {formData.type === 'Other' ? (
          <input
            type="text"
            style={styles.smallInput}
            value={customEventType || ''}
            onChange={(e) => {
              if (handleCustomEventTypeChange) {
                handleCustomEventTypeChange(e);
              } else {
                handleBasicChange('type', e.target.value);
              }
            }}
            placeholder="Enter custom event type"
          />
        ) : (
          <select
            style={styles.smallInput}
            value={formData.type || ''}
            onChange={(e) => {
              if (handleEventTypeChange) {
                handleEventTypeChange({ target: { value: e.target.value } });
              } else {
                handleBasicChange('type', e.target.value);
              }
            }}
          >
            <option value="">Select event type</option>
            {eventTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );

  const renderPackagesTab = () => (
    <div>
      <div style={styles.packagesGrid}>
        {packages && packages.map((pkg, index) => {
          const durationDays = computePackageDurationDays(pkg);

          return (
          <div key={index} style={styles.packageCard}>
            {/* Delete icon positioned in the top-right corner of the card */}
            {removePackage && index > 0 && (
              <button
                type="button"
                onClick={() => removePackage(index)}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  border: 'none',
                  background: 'transparent',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
                aria-label={`Remove package ${index + 1}`}
                title={`Remove package ${index + 1}`}
              >
                <FaTrash />
              </button>
            )}

            <div style={styles.packageHeader}>
              <input
                type="text"
                className="package-name-input"
                style={styles.packageNameInput}
                value={pkg.packageType || ''}
                onChange={(e) => handlePackageFieldChange(index, 'packageType', e.target.value)}
                placeholder="Package Name"
              />

              <div style={styles.packageTicketsContainer}>
                <span>Tickets</span>
                <input
                  type="number"
                  min="0"
                  style={styles.packageTicketsInput}
                  value={pkg.numberOfTickets ?? ''}
                  onChange={(e) => handlePackageFieldChange(index, 'numberOfTickets', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <textarea
              ref={(textarea) => {
                if (textarea) {
                  autoResizeTextarea(textarea);
                  textarea.addEventListener('input', () => autoResizeTextarea(textarea));
                }
              }}
              style={styles.packageDescription}
              value={pkg.details || ''}
              onChange={(e) => {
                handlePackageFieldChange(index, 'details', e.target.value);
                if (e.target) {
                  autoResizeTextarea(e.target);
                }
              }}
              placeholder="Describe what is included in this package"
            />

            <div style={styles.packageDetails}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', margin: '6px 0 2px 0' }}>
                Select sale dates of the package
              </div>
              <div className="editable-event-pair-row" style={{ ...styles.packageDetail, marginLeft: '0px' }}>
                <div style={{ display: 'flex', gap: '0px', flex: 1 }}>
                  <input
                    type="date"
                    style={{ ...styles.packageDetailInput, flex: 1 }}
                    value={pkg.startDate || ''}
                    onChange={(e) => handlePackageFieldChange(index, 'startDate', e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    style={{ ...styles.packageDetailInput, flex: 1 }}
                    value={pkg.endDate || ''}
                    onChange={(e) => handlePackageFieldChange(index, 'endDate', e.target.value)}
                  />
                </div>
              </div>

              {/* Validity date range for the package */}
              <div style={{ fontSize: '0.8rem', color: '#6c757d', margin: '6px 0 2px 0', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap', minWidth: '300px' }}>
                <span>Select dates for which package is valid</span>
                {durationDays && (
                  <span style={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap', marginLeft: '4px' }}>
                    ({durationDays} day{durationDays > 1 ? 's' : ''})
                  </span>
                )}
              </div>
              <div className="editable-event-pair-row" style={{ ...styles.packageDetail, marginLeft: '0px' }}>
                <div style={{ display: 'flex', gap: '0px', alignItems: 'center', flex: 1 }}>
                  <input
                    type="date"
                    min={formData.startdate || ''}
                    max={formData.enddate || ''}
                    style={{ ...styles.packageDetailInput, flex: 1 }}
                    value={pkg.validFromDate || ''}
                    onChange={(e) => handlePackageFieldChange(index, 'validFromDate', e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    min={pkg.validFromDate || formData.startdate || ''}
                    max={formData.enddate || ''}
                    style={{ ...styles.packageDetailInput, flex: 1 }}
                    value={pkg.validToDate || ''}
                    onChange={(e) => handlePackageFieldChange(index, 'validToDate', e.target.value)}
                  />
                </div>
              </div>

              {/* Validity time range for the package */}
              <div style={{ fontSize: '0.8rem', color: '#6c757d', margin: '6px 0 2px 0' }}>
                Select time for which package is valid
              </div>
              <div className="editable-event-pair-row" style={{ ...styles.packageDetail, marginLeft: '0px' }}>
                <div style={{ display: 'flex', gap: '0px', flex: 1 }}>
                  <input
                    type="time"
                    style={{ ...styles.packageDetailInput, flex: 1, height: '23px' }}
                    value={pkg.validFromTime || ''}
                    onChange={(e) => handlePackageFieldChange(index, 'validFromTime', e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="time"
                    style={{ ...styles.packageDetailInput, flex: 1, height: '23px' }}
                    value={pkg.validToTime || ''}
                    onChange={(e) => handlePackageFieldChange(index, 'validToTime', e.target.value)}
                  />
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.5rem',
                fontSize: '0.85rem',
                color: '#6c757d',
              }}>
                <span>Price:</span>
                <input
                  type="text"
                  style={styles.packagePriceInput}
                  value={pkg.pricing || ''}
                  onChange={(e) => handlePackageFieldChange(index, 'pricing', e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div style={styles.packageBottom} />
          </div>
        )})}

        {addPackage && (
          <button
            type="button"
            style={{ ...styles.packageCard, ...styles.addPackageCard }}
            onClick={() => addPackage()}
          >
            <FaPlus style={{ fontSize: '1.2rem' }} />
            <span style={{ fontWeight: 600 }}>Add Package</span>
          </button>
        )}
      </div>
    </div>
  );

  const renderTermsTab = () => (
    <div>
      <h3>Terms and Conditions</h3>
      <textarea
        style={styles.eventDescription}
        value={formData.terms_and_conditions || ''}
        onChange={(e) => handleBasicChange('terms_and_conditions', e.target.value)}
        placeholder="Enter the terms and conditions that attendees must accept."
      />
    </div>
  );

  const renderInfoTab = () => (
    <div>
      <h3>Additional Information</h3>
      <div style={styles.eventInfo}>
        <div style={styles.eventInfoItem}>
          <FaHome />
          <input
            type="text"
            style={styles.editableInfoInput}
            value={formData.organizer || ''}
            onChange={(e) => handleBasicChange('organizer', e.target.value)}
            placeholder="Organizer name"
          />
        </div>
        <div style={styles.eventInfoItem}>
          <FaTicketAlt />
          <input
            type="text"
            style={styles.editableInfoInput}
            value={formData.category || ''}
            onChange={(e) => handleBasicChange('category', e.target.value)}
            placeholder="Category"
          />
        </div>
        <div style={styles.eventInfoItem}>
          <FaUser />
          <input
            type="number"
            min="1"
            style={styles.editableInfoInput}
            value={formData.capacity || ''}
            onChange={(e) => handleBasicChange('capacity', e.target.value)}
            placeholder="Capacity"
          />
        </div>
        <div style={styles.eventInfoItem}>
          <FaInfoCircle />
          <input
            type="text"
            style={styles.editableInfoInput}
            value={formData.status || ''}
            onChange={(e) => handleBasicChange('status', e.target.value)}
            placeholder="Status (e.g. Draft, Pending Approval)"
          />
        </div>
      </div>

      <div style={styles.contactInfo}>
        <h4>Contact Information</h4>
        <p>Provide contact details for customers to reach the organiser.</p>
        <div style={styles.contactItem}>
          <strong>Email:</strong>
          <input
            type="email"
            style={styles.contactInput}
            value={formData.contact_email || ''}
            onChange={(e) => handleBasicChange('contact_email', e.target.value)}
            placeholder="organiser@example.com"
          />
        </div>
        <div style={styles.contactItem}>
          <strong>Phone:</strong>
          <input
            type="tel"
            style={styles.contactInput}
            value={formData.contact_phone || ''}
            onChange={(e) => handleBasicChange('contact_phone', e.target.value)}
            placeholder="+27 00 000 0000"
          />
        </div>
      </div>
    </div>
  );

  const renderActiveTabContent = () => {
    const index = Number.isInteger(activeTab) && activeTab >= 0 ? activeTab : 0;

    if (index === 1) return renderPackagesTab();
    if (index === 2) return renderTermsTab();

    // Custom tabs start from index 3
    if (index >= 3) {
      const customIndex = index - 3;
      const tab = tabs && tabs[customIndex];
      if (!tab) return renderAboutTab();

      return (
        <div>
          <h3>{tab.name || `Tab ${customIndex + 1}`}</h3>
          <textarea
            style={styles.eventDescription}
            value={tab.content || ''}
            onChange={(e) => handleTabContentChange(customIndex, e.target.value)}
            placeholder="Tab content"
          />
        </div>
      );
    }

    // index 0 or fallback -> Information
    return renderAboutTab();
  };

  return (
    <>
      <div className="editable-event-card" style={styles.eventCard}>
        {/* Image area; upload/drag-drop only when onImageChange is provided */}
        <div
          style={{
            ...styles.eventImageContainer,
            // When an image is present, give the container more vertical space
            height: coverImageFile ? '320px' : styles.eventImageContainer.height,
            ...imageBackgroundStyle,
          }}
          onDragOver={isImageEditable ? (e) => {
            e.preventDefault();
          } : undefined}
          onDrop={isImageEditable ? (e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file) {
              const syntheticEvent = { target: { files: [file] } };
              onImageChange(syntheticEvent);
            }
          } : undefined}
        >
          <EventImage
            image={coverImageUrl}
            coverimage={coverImageUrl}
            alt={formData.name || 'Event cover image'}
            style={styles.eventImage}
            loading="eager"
          />
          {isImageEditable && (
            <label style={styles.imageDropZone}>
              <span style={{ fontWeight: 600 }}>Click or drop an image here</span>
              <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                Recommended: PNG or JPEG
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={onImageChange}
              />
            </label>
          )}
        </div>

        {/* Background controls under image (location editing stays in About / Information tab) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            padding: '0.75rem 1.5rem 0.25rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#374151' }}>
            <span>Mode:</span>
            <select
              value={backgroundMode}
              onChange={(e) => setBackgroundMode(e.target.value)}
              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
            >
              <option value="solid">Solid</option>
              <option value="gradient">Gradient</option>
              <option value="image">Image</option>
            </select>

            {backgroundMode === 'solid' && (
              <>
                <span>Color:</span>
                <input
                  type="color"
                  value={imageBgColor}
                  onChange={(e) => setImageBgColor(e.target.value)}
                  style={{ width: '36px', height: '20px', padding: 0, border: 'none', cursor: 'pointer' }}
                />
              </>
            )}

            {backgroundMode === 'gradient' && (
              <>
                <span>From:</span>
                <input
                  type="color"
                  value={imageBgColor}
                  onChange={(e) => setImageBgColor(e.target.value)}
                  style={{ width: '36px', height: '20px', padding: 0, border: 'none', cursor: 'pointer' }}
                />
                <span>To:</span>
                <input
                  type="color"
                  value={imageBgColor2}
                  onChange={(e) => setImageBgColor2(e.target.value)}
                  style={{ width: '36px', height: '20px', padding: 0, border: 'none', cursor: 'pointer' }}
                />
              </>
            )}

            {backgroundMode === 'image' && (
              <>
                <span>Image:</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const url = URL.createObjectURL(file);
                      setBgImageUrl(url);
                      if (onBackgroundImageChange) {
                        onBackgroundImageChange({ target: { files: [file] } });
                      }
                    }
                  }}
                  style={{ fontSize: '0.7rem' }}
                />
              </>
            )}
          </div>
        </div>

        {/* Tabs header + content */}
        {renderTabsHeader()}

        <div style={styles.tabContent}>{renderActiveTabContent()}</div>
      </div>
    </>
  );
};

export default EditableCustomerEventCard;
